import React, { useState, useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useWorkspaceStore, selectActiveWorkspace } from '@/stores/workspaceStore';
import { useSemanticSearch } from '@/hooks/useAI';
import { useSearchCapabilities } from '@/hooks/useCapabilities';
import { searchCapabilitiesLocally } from '@/lib/db';
import AppShell from '@/components/layout/AppShell';
import CapabilityCard from '@/components/capabilities/CapabilityCard';
import type { Capability } from '@/types';

// ─── SearchPage ───────────────────────────────────────────────────────────────

const SearchPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const workspaceId = activeWorkspace?.id ?? null;

  const params = new URLSearchParams(location.search);
  const initialQuery = params.get('q') ?? '';

  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const [searchMode, setSearchMode] = useState<'text' | 'semantic'>('text');
  const [localResults, setLocalResults] = useState<Capability[]>([]);

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Update URL
  useEffect(() => {
    const next = debouncedQuery ? `?q=${encodeURIComponent(debouncedQuery)}` : '';
    history.replace(`/search${next}`);
  }, [debouncedQuery, history]);

  // Text search (API)
  const {
    data: textResults = [],
    isLoading: textLoading,
  } = useSearchCapabilities(workspaceId, debouncedQuery);

  // Semantic search (AI)
  const {
    data: semanticResult,
    isLoading: semanticLoading,
  } = useSemanticSearch(workspaceId, debouncedQuery, searchMode === 'semantic');

  // Local fallback search
  const loadLocalResults = useCallback(async () => {
    if (!workspaceId || !debouncedQuery) {
      setLocalResults([]);
      return;
    }
    const results = await searchCapabilitiesLocally(workspaceId, debouncedQuery);
    setLocalResults(results);
  }, [workspaceId, debouncedQuery]);

  useEffect(() => {
    if (searchMode === 'text') {
      loadLocalResults();
    }
  }, [searchMode, loadLocalResults]);

  const isLoading = searchMode === 'text' ? textLoading : semanticLoading;

  const displayResults: Capability[] =
    searchMode === 'semantic'
      ? [] // semantic results are shown differently
      : textResults.length > 0
      ? textResults
      : localResults;

  const semanticItems = semanticResult?.results ?? [];

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-12 space-y-6">
            {/* Header */}
            <div>
              <h1 className="text-2xl font-bold text-surface-900 dark:text-white">Search</h1>
              <p className="text-sm text-surface-500 mt-0.5">
                Find capabilities by keyword or use AI-powered semantic search
              </p>
            </div>

            {/* Search bar */}
            <div className="relative">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                placeholder={
                  searchMode === 'semantic'
                    ? 'Ask anything, e.g. "skills for leading engineering teams"…'
                    : 'Search capabilities by title, tag, or summary…'
                }
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                className="w-full pl-11 pr-4 py-3 text-sm rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900 shadow-card"
              />
              {isLoading && (
                <svg
                  className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin w-4 h-4 text-primary-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
            </div>

            {/* Mode switcher */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-surface-500">Mode:</span>
              <div className="flex rounded-lg bg-surface-100 dark:bg-surface-800 p-0.5">
                {(['text', 'semantic'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setSearchMode(mode)}
                    className={[
                      'px-3 py-1 rounded-md text-xs font-medium transition-all',
                      searchMode === mode
                        ? 'bg-white dark:bg-surface-700 text-primary-700 dark:text-primary-300 shadow-card'
                        : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300',
                    ].join(' ')}
                  >
                    {mode === 'text' ? 'Keyword' : 'Semantic AI'}
                  </button>
                ))}
              </div>
            </div>

            {/* Results */}
            {!debouncedQuery ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 rounded-xl bg-surface-800 border border-surface-700 flex items-center justify-center mb-3 mx-auto">
                  <svg className="w-6 h-6 text-surface-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                </div>
                <p className="text-base font-medium text-surface-600 dark:text-surface-400">
                  Start typing to search
                </p>
                <p className="text-sm text-surface-400 mt-1">
                  Try searching for "leadership", "Python", or "machine learning"
                </p>
              </div>
            ) : searchMode === 'semantic' ? (
              <div className="space-y-4">
                {isLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="h-24 rounded-xl bg-surface-100 dark:bg-surface-800 animate-pulse" />
                    ))}
                  </div>
                ) : semanticItems.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-sm text-surface-500">No semantic matches found</p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-surface-400">
                      {semanticItems.length} AI-matched results
                    </p>
                    {semanticItems.map((item) => (
                      <div
                        key={item.id}
                        className="bg-white dark:bg-surface-900 rounded-xl border border-surface-200 dark:border-surface-800 shadow-card p-4 hover:shadow-card-hover hover:border-primary-300 dark:hover:border-primary-700 transition-all cursor-pointer"
                        onClick={() => {
                          if (item.capabilityId) {
                            history.push(`/capabilities/${item.capabilityId}`);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-surface-900 dark:text-white">
                              {item.title}
                            </h3>
                            <p className="text-xs text-surface-500 mt-1 leading-relaxed">
                              {item.summary}
                            </p>
                            {item.reasoning && (
                              <p className="text-xs text-primary-600 dark:text-primary-400 mt-2 italic">
                                "{item.reasoning}"
                              </p>
                            )}
                            {item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-1.5 py-0.5 rounded text-2xs bg-surface-100 dark:bg-surface-800 text-surface-500"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <span
                              className="text-xs font-bold text-accent-600 dark:text-accent-400"
                              title="Relevance score"
                            >
                              {Math.round(item.score * 100)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {isLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="h-28 rounded-xl bg-surface-100 dark:bg-surface-800 animate-pulse" />
                    ))}
                  </div>
                ) : displayResults.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-sm text-surface-500">
                      No capabilities found for "{debouncedQuery}"
                    </p>
                    <button
                      type="button"
                      onClick={() => setSearchMode('semantic')}
                      className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Try semantic search instead
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-surface-400">
                      {displayResults.length} results for "{debouncedQuery}"
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {displayResults.map((cap: Capability) => (
                        <CapabilityCard
                          key={cap.id}
                          capability={cap}
                          onClick={() => history.push(`/capabilities/${cap.id}`)}
                          highlight={debouncedQuery}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
      </div>
    </AppShell>
  );
};

export default SearchPage;
